import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';

// ─── Styles ──────────────────────────────────────────────────────────────────
const useStyles = makeStyles(() => ({
  root: {
    padding: 24,
    maxWidth: 700,
  },
  title: {
    fontWeight: 700,
    fontSize: '1.15rem',
    color: '#333',
    marginBottom: 16,
  },
  card: {
    padding: 16,
    backgroundColor: '#fafafa',
    border: '1px solid #e0e0e0',
    borderRadius: 6,
    marginBottom: 16,
  },
  cardTitle: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: '#555',
    marginBottom: 10,
  },
  fieldRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '4px 0',
  },
  fieldLabel: {
    fontSize: '0.85rem',
    color: '#777',
  },
  fieldValue: {
    fontSize: '0.85rem',
    fontWeight: 500,
    color: '#333',
  },
  confirmText: {
    fontSize: '0.95rem',
    color: '#333',
    margin: '16px 0 20px',
    textAlign: 'center',
    fontWeight: 500,
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
    marginTop: 8,
  },
  cancelBtn: {
    textTransform: 'none',
    fontWeight: 500,
  },
  confirmBtn: {
    textTransform: 'none',
    fontWeight: 600,
    backgroundColor: '#4CAF50',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#43A047',
    },
  },
}));

/**
 * Simplified view for linking a surgery to an existing admission.
 *
 * Props:
 *  - surgery: { procedure, scheduled_date, lead_surgeon }
 *  - admissionDetails: { ward, bed, date, doctor }
 *  - onConfirm: callback
 *  - onCancel: callback
 */
const LinkAdmissionView = ({ surgery, admissionDetails, onConfirm, onCancel }) => {
  const classes = useStyles();

  const admission = admissionDetails || {};
  const surg = surgery || {};

  return (
    <Paper className={classes.root} elevation={1}>
      <Typography className={classes.title}>
        Link Surgery to Existing Admission
      </Typography>
      <Divider style={{ marginBottom: 16 }} />

      {/* Current Admission Card */}
      <div className={classes.card}>
        <Typography className={classes.cardTitle}>Current Admission</Typography>
        <div className={classes.fieldRow}>
          <Typography className={classes.fieldLabel}>Ward</Typography>
          <Typography className={classes.fieldValue}>
            {admission.ward || '—'}
          </Typography>
        </div>
        <div className={classes.fieldRow}>
          <Typography className={classes.fieldLabel}>Bed Number</Typography>
          <Typography className={classes.fieldValue}>
            {admission.bed || '—'}
          </Typography>
        </div>
        <div className={classes.fieldRow}>
          <Typography className={classes.fieldLabel}>Admission Date</Typography>
          <Typography className={classes.fieldValue}>
            {admission.date || '—'}
          </Typography>
        </div>
        <div className={classes.fieldRow}>
          <Typography className={classes.fieldLabel}>Attending Doctor</Typography>
          <Typography className={classes.fieldValue}>
            {admission.doctor || '—'}
          </Typography>
        </div>
      </div>

      {/* Surgery Details Card */}
      <div className={classes.card}>
        <Typography className={classes.cardTitle}>Surgery Details</Typography>
        <div className={classes.fieldRow}>
          <Typography className={classes.fieldLabel}>Procedure</Typography>
          <Typography className={classes.fieldValue}>
            {surg.procedure || '—'}
          </Typography>
        </div>
        <div className={classes.fieldRow}>
          <Typography className={classes.fieldLabel}>Date</Typography>
          <Typography className={classes.fieldValue}>
            {surg.scheduled_date || '—'}
          </Typography>
        </div>
        <div className={classes.fieldRow}>
          <Typography className={classes.fieldLabel}>Surgeon</Typography>
          <Typography className={classes.fieldValue}>
            {surg.lead_surgeon || '—'}
          </Typography>
        </div>
      </div>

      <Typography className={classes.confirmText}>
        Link this surgery to the current admission?
      </Typography>

      <div className={classes.buttonRow}>
        <Button
          variant="outlined"
          className={classes.cancelBtn}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          className={classes.confirmBtn}
          onClick={onConfirm}
        >
          Confirm Link
        </Button>
      </div>
    </Paper>
  );
};

export default LinkAdmissionView;
